import { useKBar, useRegisterActions } from 'kbar'
import { useCallback, useMemo, useRef } from 'react'
import { toast } from 'react-hot-toast'
import { useProblemSearch } from '../../hooks/use-problem-search'
import { useCoachingQuestions } from '../../hooks/use-questions'
import { useCommandPaletteState } from '../context'

/**
 * Single component that registers ALL command palette actions.
 * This avoids issues with multiple useRegisterActions calls not cleaning up properly.
 */
export function AllActions() {
  const {
    mode,
    startMode,
    cancelMode,
    addProblem,
    selectSeries,
    selectProblemToRemove,
    saveSeries,
    removeFromSeries,
    allSeries,
    selectedSeries,
    seriesProblems,
    problemIdsInSeries,
    lockedSeriesId,
    problemsToRemove,
    mergePhase,
    mergePrimaryProblem,
    selectMergePrimary,
    selectMergeDuplicate,
    confirmMerge,
    cancelMerge,
    followUpPhase,
    followUpTargetProblem,
    followUpExistingFollowUps,
    followUpInput,
    selectFollowUpProblem,
    setFollowUpPrompt,
    setFollowUpTimeComplexity,
    saveFollowUp,
    cancelFollowUp,
  } = useCommandPaletteState()

  const { questions } = useCoachingQuestions()
  const { search, minSearchLength } = useProblemSearch(questions)
  const { searchQuery, query } = useKBar((state) => ({
    searchQuery: state.searchQuery,
  }))

  // Use ref to avoid recreating callbacks on every render
  const queryRef = useRef(query)
  queryRef.current = query

  // Helper to keep palette open after an action - stable reference
  const keepPaletteOpen = useCallback((clearSearch = false) => {
    requestAnimationFrame(() => {
      queryRef.current.toggle()
      if (clearSearch) {
        // Reset the nested action context so we're back at root level
        queryRef.current.setCurrentRootAction(null)
        setTimeout(() => {
          const input = document.getElementById(
            'kbar-search-input',
          ) as HTMLInputElement | null
          if (input) {
            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
              window.HTMLInputElement.prototype,
              'value',
            )?.set
            nativeInputValueSetter?.call(input, '')
            input.dispatchEvent(new Event('input', { bubbles: true }))
          }
          queryRef.current.setSearch('')
        }, 50)
      }
    })
  }, [])

  const actions = useMemo(() => {
    const allActions: Array<{
      id: string
      name: string
      perform?: () => void
      section?: string
      priority?: number
      keywords?: string
      parent?: string
      sourceIcon?: string | null
      source?: string
    }> = []

    // === IDLE MODE: Show parent actions and their children ===
    if (mode === 'idle') {
      // Parent actions
      allActions.push(
        {
          id: 'series',
          name: 'Series',
          keywords: 'series group thematic problems',
          section: 'Problem Relationships',
        },
        {
          id: 'merge',
          name: 'Link Duplicate Problems',
          keywords: 'link duplicate same question combine dedup',
          section: 'Problem Relationships',
        },
        {
          id: 'variants',
          name: 'Problem Variants',
          keywords: 'variant canonical company link problems',
          section: 'Problem Relationships',
        },
      )

      // Series child actions
      allActions.push(
        {
          id: 'create-series',
          name: 'Create New Series',
          keywords: 'create new series group',
          parent: 'series',
          perform: () => {
            startMode('create-series')
            keepPaletteOpen(true)
          },
        },
        {
          id: 'add-to-series',
          name: 'Add Problem to Existing Series',
          keywords: 'add existing series',
          parent: 'series',
          perform: () => {
            startMode('select-series')
            keepPaletteOpen(true)
          },
        },
        {
          id: 'remove-from-series',
          name: 'Remove Problem from Series',
          keywords: 'remove delete unlink series',
          parent: 'series',
          perform: () => {
            startMode('remove-from-series')
            keepPaletteOpen(true)
          },
        },
      )

      // Merge child action
      allActions.push({
        id: 'start-merge',
        name: 'Link Duplicate Problems',
        keywords: 'link duplicate same question combine dedup',
        parent: 'merge',
        perform: () => {
          startMode('merge')
          keepPaletteOpen(true)
        },
      })

      // Variant child actions
      allActions.push(
        {
          id: 'link-variant',
          name: 'Link Problem Variant',
          keywords: 'link variant canonical company',
          parent: 'variants',
          perform: () => {
            toast.error('Variant linking coming soon')
          },
        },
        {
          id: 'unlink-variant',
          name: 'Unlink Problem Variant',
          keywords: 'unlink variant remove',
          parent: 'variants',
          perform: () => {
            toast.error('Variant unlinking coming soon')
          },
        },
      )

      // Follow-ups parent action
      allActions.push({
        id: 'follow-ups',
        name: 'Follow-ups',
        keywords: 'follow-up extension constraint challenge',
        section: 'Problem Relationships',
      })

      // Follow-up child actions
      allActions.push({
        id: 'add-follow-up',
        name: 'Add Follow-up to Problem',
        keywords: 'add follow-up extension constraint O(1) space single pass',
        parent: 'follow-ups',
        perform: () => {
          startMode('add-follow-up')
          keepPaletteOpen(true)
        },
      })

      return allActions
    }

    // === SELECT SERIES MODE ===
    if (mode === 'select-series') {
      const queryLower = searchQuery.toLowerCase()
      const filteredSeries = allSeries.filter((s) =>
        s.name.toLowerCase().includes(queryLower),
      )

      for (const series of filteredSeries) {
        allActions.push({
          id: `select-series-${series.id}`,
          name: `${series.name} (${series.members.length} problems)`,
          keywords: series.name,
          perform: () => {
            selectSeries(series)
            keepPaletteOpen(true)
          },
          section: 'Select Series',
        })
      }

      allActions.push({
        id: 'cancel-series-select',
        name: 'Cancel',
        perform: cancelMode,
        section: 'Actions',
      })
    }

    // === CREATE/ADD TO SERIES MODE ===
    if (mode === 'create-series' || mode === 'add-to-series') {
      const searchResults = search(searchQuery)
      const selectedSeriesProblemIds = new Set(
        selectedSeries?.members.map((m) => m.problemId) ?? [],
      )

      const filteredResults = searchResults.filter((result) => {
        if (seriesProblems.some((p) => p.id === result.item.id)) return false
        if (mode === 'create-series') {
          return !problemIdsInSeries.has(result.item.id)
        }
        return !selectedSeriesProblemIds.has(result.item.id)
      })

      for (const result of filteredResults) {
        allActions.push({
          id: `add-problem-${result.item.id}`,
          name: result.item.externalId
            ? `#${result.item.externalId} - ${result.item.name}`
            : result.item.name,
          perform: () => {
            addProblem(result.item)
            keepPaletteOpen(true)
          },
          section: 'Search Results',
          sourceIcon: result.item.sourceIcon,
          source: result.item.source,
        })
      }

      const minToSave = mode === 'create-series' ? 2 : 1
      if (seriesProblems.length >= minToSave) {
        const saveLabel =
          mode === 'create-series'
            ? `Save Series (${seriesProblems.length} problems)`
            : `Add ${seriesProblems.length} Problem${seriesProblems.length > 1 ? 's' : ''} to ${selectedSeries?.name}`

        allActions.push({
          id: 'save-series',
          name: saveLabel,
          perform: saveSeries,
          section: 'Actions',
          priority: 1,
        })
      }

      allActions.push({
        id: 'cancel-series',
        name: 'Cancel',
        perform: cancelMode,
        section: 'Actions',
      })

      if (
        searchQuery.length > 0 &&
        searchQuery.length < minSearchLength &&
        filteredResults.length === 0
      ) {
        allActions.push({
          id: 'search-hint',
          name: `Type ${minSearchLength - searchQuery.length} more character(s) to search...`,
          perform: () => {},
          section: 'Search',
        })
      }
    }

    // === REMOVE FROM SERIES MODE ===
    if (mode === 'remove-from-series') {
      const queryLower = searchQuery.toLowerCase()
      const problemToSeriesMap = new Map<
        string,
        Array<{ seriesId: string; seriesName: string; problemTitle: string }>
      >()

      for (const series of allSeries) {
        if (lockedSeriesId && series.id !== lockedSeriesId) continue

        for (const member of series.members) {
          if (
            problemsToRemove.some(
              (p) =>
                p.problemId === member.problemId && p.seriesId === series.id,
            )
          ) {
            continue
          }

          const existing = problemToSeriesMap.get(member.problemId) ?? []
          existing.push({
            seriesId: series.id,
            seriesName: series.name,
            problemTitle: member.problemTitle,
          })
          problemToSeriesMap.set(member.problemId, existing)
        }
      }

      for (const [problemId, seriesEntries] of problemToSeriesMap) {
        const problemTitle = seriesEntries[0].problemTitle

        if (
          queryLower &&
          !problemTitle.toLowerCase().includes(queryLower) &&
          !seriesEntries.some((e) =>
            e.seriesName.toLowerCase().includes(queryLower),
          )
        ) {
          continue
        }

        if (seriesEntries.length === 1 || lockedSeriesId) {
          const entry = lockedSeriesId
            ? seriesEntries.find((e) => e.seriesId === lockedSeriesId)
            : seriesEntries[0]

          if (entry) {
            const seriesNames = seriesEntries
              .map((e) => e.seriesName)
              .join(', ')
            const displayName = lockedSeriesId
              ? problemTitle
              : `${problemTitle} (${seriesNames})`

            allActions.push({
              id: `remove-problem-${problemId}-${entry.seriesId}`,
              name: displayName,
              keywords: `${problemTitle} ${seriesNames}`,
              perform: () => {
                selectProblemToRemove(
                  problemId,
                  entry.seriesId,
                  entry.problemTitle,
                )
                keepPaletteOpen(true)
              },
              section: 'Problems in Series',
            })
          }
        } else {
          for (const entry of seriesEntries) {
            allActions.push({
              id: `remove-problem-${problemId}-${entry.seriesId}`,
              name: `${problemTitle} → Remove from "${entry.seriesName}"`,
              keywords: `${problemTitle} ${seriesEntries.map((e) => e.seriesName).join(', ')}`,
              perform: () => {
                selectProblemToRemove(
                  problemId,
                  entry.seriesId,
                  entry.problemTitle,
                )
                keepPaletteOpen(true)
              },
              section: `${problemTitle} (in ${seriesEntries.length} series)`,
            })
          }
        }
      }

      if (problemsToRemove.length >= 1) {
        allActions.push({
          id: 'confirm-remove',
          name: `Remove ${problemsToRemove.length} Problem${problemsToRemove.length > 1 ? 's' : ''} from Series`,
          perform: removeFromSeries,
          section: 'Actions',
          priority: 1,
        })
      }

      allActions.push({
        id: 'cancel-remove',
        name: 'Cancel',
        perform: cancelMode,
        section: 'Actions',
      })
    }

    // === MERGE MODE ===
    if (mode === 'merge') {
      if (
        mergePhase === 'select-primary' ||
        mergePhase === 'select-duplicate'
      ) {
        const searchResults = search(searchQuery)

        const filteredResults = searchResults.filter((result) => {
          if (
            mergePhase === 'select-duplicate' &&
            mergePrimaryProblem &&
            result.item.id === mergePrimaryProblem.id
          ) {
            return false
          }
          return true
        })

        for (const result of filteredResults) {
          allActions.push({
            id: `merge-select-${result.item.id}`,
            name: result.item.externalId
              ? `#${result.item.externalId} - ${result.item.name}`
              : result.item.name,
            perform: () => {
              if (mergePhase === 'select-primary') {
                selectMergePrimary(result.item)
              } else {
                selectMergeDuplicate(result.item)
              }
              keepPaletteOpen(true)
            },
            section: 'Search Results',
            sourceIcon: result.item.sourceIcon,
            source: result.item.source,
          })
        }

        if (
          searchQuery.length > 0 &&
          searchQuery.length < minSearchLength &&
          filteredResults.length === 0
        ) {
          allActions.push({
            id: 'merge-search-hint',
            name: `Type ${minSearchLength - searchQuery.length} more character(s) to search...`,
            perform: () => {},
            section: 'Search',
          })
        }
      } else if (mergePhase === 'preview') {
        allActions.push({
          id: 'confirm-merge',
          name: 'Confirm Link',
          perform: confirmMerge,
          section: 'Actions',
          priority: 1,
        })
      }

      allActions.push({
        id: 'cancel-merge',
        name: 'Cancel Link',
        perform: cancelMerge,
        section: 'Actions',
      })
    }

    // === ADD FOLLOW-UP MODE ===
    if (mode === 'add-follow-up') {
      if (followUpPhase === 'select-problem') {
        // Phase 1: Search and select a problem
        const searchResults = search(searchQuery)

        for (const result of searchResults) {
          allActions.push({
            id: `followup-select-${result.item.id}`,
            name: result.item.externalId
              ? `#${result.item.externalId} - ${result.item.name}`
              : result.item.name,
            perform: () => {
              selectFollowUpProblem(result.item)
              keepPaletteOpen(true)
            },
            section: 'Select Problem',
            sourceIcon: result.item.sourceIcon,
            source: result.item.source,
          })
        }

        if (
          searchQuery.length > 0 &&
          searchQuery.length < minSearchLength &&
          searchResults.length === 0
        ) {
          allActions.push({
            id: 'followup-search-hint',
            name: `Type ${minSearchLength - searchQuery.length} more character(s) to search...`,
            perform: () => {},
            section: 'Search',
          })
        }
      } else if (followUpPhase === 'enter-prompt') {
        // Phase 2: Enter the follow-up prompt
        // Show existing follow-ups for reference
        if (followUpExistingFollowUps.length > 0) {
          for (const existingFollowUp of followUpExistingFollowUps) {
            allActions.push({
              id: `existing-followup-${existingFollowUp.id}`,
              name: `${existingFollowUp.order + 1}. ${existingFollowUp.prompt}`,
              perform: () => {},
              section: 'Existing Follow-ups (reference)',
            })
          }
        }

        // Next action - uses the search query as the prompt
        if (searchQuery.trim().length > 0) {
          allActions.push({
            id: 'set-followup-prompt',
            name: `Next: "${searchQuery.trim().substring(0, 50)}${searchQuery.trim().length > 50 ? '...' : ''}"`,
            perform: () => {
              setFollowUpPrompt(searchQuery.trim())
              keepPaletteOpen(true)
            },
            section: 'Actions',
            priority: 1,
          })
        } else {
          allActions.push({
            id: 'followup-prompt-hint',
            name: 'Type the follow-up prompt (e.g., "Can you do it in O(1) space?")',
            perform: () => {},
            section: 'Instructions',
          })
        }
      } else if (followUpPhase === 'enter-time-complexity') {
        // Phase 3: Enter time complexity
        const timeComplexityValue = searchQuery.trim() || null

        allActions.push({
          id: 'set-time-complexity',
          name: timeComplexityValue
            ? `Next: Time = ${timeComplexityValue}`
            : 'Next: Skip time complexity',
          perform: () => {
            setFollowUpTimeComplexity(timeComplexityValue)
            keepPaletteOpen(true)
          },
          section: 'Actions',
          priority: 1,
        })

        allActions.push({
          id: 'time-complexity-hint',
          name: 'Enter time complexity (e.g., "O(n)", "O(1)") or leave empty to skip',
          perform: () => {},
          section: 'Instructions',
        })
      } else if (followUpPhase === 'enter-space-complexity') {
        // Phase 4: Enter space complexity and save
        const spaceComplexityValue = searchQuery.trim() || null

        allActions.push({
          id: 'save-follow-up',
          name: spaceComplexityValue
            ? `Save: Space = ${spaceComplexityValue}`
            : 'Save: No space complexity',
          perform: () => {
            saveFollowUp(spaceComplexityValue)
          },
          section: 'Actions',
          priority: 1,
        })

        allActions.push({
          id: 'space-complexity-hint',
          name: 'Enter space complexity (e.g., "O(1)", "O(n)") or press ⌘+Enter to save',
          perform: () => {},
          section: 'Instructions',
        })
      }

      allActions.push({
        id: 'cancel-follow-up',
        name: 'Cancel',
        perform: cancelFollowUp,
        section: 'Actions',
      })
    }

    return allActions
  }, [
    mode,
    searchQuery,
    allSeries,
    selectedSeries,
    seriesProblems,
    problemIdsInSeries,
    lockedSeriesId,
    problemsToRemove,
    mergePhase,
    mergePrimaryProblem,
    followUpPhase,
    followUpExistingFollowUps,
    startMode,
    cancelMode,
    keepPaletteOpen,
    search,
    minSearchLength,
    addProblem,
    selectSeries,
    selectProblemToRemove,
    saveSeries,
    removeFromSeries,
    selectMergePrimary,
    selectMergeDuplicate,
    confirmMerge,
    cancelMerge,
    selectFollowUpProblem,
    setFollowUpPrompt,
    setFollowUpTimeComplexity,
    saveFollowUp,
    cancelFollowUp,
  ])

  // Single registration point for all actions
  useRegisterActions(actions, [
    mode,
    mergePhase,
    followUpPhase,
    followUpInput.prompt,
    searchQuery,
    allSeries.length,
    seriesProblems.length,
    problemsToRemove.length,
    lockedSeriesId,
    selectedSeries?.id,
    mergePrimaryProblem?.id,
    followUpTargetProblem?.id,
    followUpExistingFollowUps.length,
  ])

  return null
}
